import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { FooterComponent } from './shared/layout/footer/footer.component';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { HttpClientModule } from '@angular/common/http';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  globalRulesCloseShift,
  globalRulesGetCloseShiftOrderSummary,
  globalRulesOpenShift,
  selectGlobalRulesCloseShiftOrderSummary,
  selectGlobalRulesHealthCheck,
  selectGlobalRulesIsCheckShiftLoading,
  selectGlobalRulesIsShiftOpen,
  selectGlobalRulesIsWrongShift,
} from './stores/global';
import { MessageService } from 'primeng/api';
import { Observable, Subject, fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, takeUntil } from 'rxjs/operators';
import { CloseShiftOrderSummary } from './models/nested/close-shift-order-summary';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AppAuthService } from './cores/auth/app-auth-service';
import { SidebarComponent } from './shared/layout/sidebar/sidebar.component';
import { DeactivationModalComponent } from './shared/layout/sidebar/deactivation-modal/deactivation-modal.component';

const TOAST_BASE_Z_INDEX = 10000;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    TranslateModule,
    RouterOutlet,
    FooterComponent,
    SidebarModule,
    ButtonModule,
    RouterModule,
    ToastModule,
    HttpClientModule,
    OverlayPanelModule,
    InputNumberModule,
    DialogModule,
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    SidebarComponent,
    DeactivationModalComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('deactivationModal') deactivationModal!: DeactivationModalComponent;

  readonly toastBaseZIndex = TOAST_BASE_Z_INDEX;

  initialCashAmount = new FormControl(0);
  endCashAmount = new FormControl(0);

  showOpenShiftDialog: boolean = false;
  showCloseShiftDialog: boolean = false;
  closeShiftOrderSummary: Observable<CloseShiftOrderSummary | null>;
  closeShiftOrderSummaryData: CloseShiftOrderSummary | null = null;
  isWrongShift: Observable<boolean>;
  isShiftOpen: Observable<boolean>;
  isShiftLoading: Observable<boolean>;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private store: Store,
    private authService: AppAuthService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private messageService: MessageService,
  ) {
    this.isWrongShift = this.store.select(selectGlobalRulesIsWrongShift);
    this.isShiftOpen = this.store.select(selectGlobalRulesIsShiftOpen);
    this.isShiftLoading = this.store.select(
      selectGlobalRulesIsCheckShiftLoading,
    );
    this.closeShiftOrderSummary = this.store.select(
      selectGlobalRulesCloseShiftOrderSummary,
    );
    this.closeShiftOrderSummary
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.closeShiftOrderSummaryData = data;
        this.cdr.markForCheck();
      });
  }

  ngOnInit() {
    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false)),
      this.store
        .select(selectGlobalRulesHealthCheck)
        .pipe(filter((passed): passed is boolean => passed !== null)),
    )
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.destroy$))
      .subscribe((isOnline) => this.notifyNetworkChange(isOnline));
  }

  private notifyNetworkChange(isOnline: boolean) {
    const prefix = isOnline ? 'common.network_online' : 'common.network_offline';
    this.messageService.add({
      severity: isOnline ? 'success' : 'warn',
      summary: this.translateService.instant(`${prefix}_title`),
      detail: this.translateService.instant(`${prefix}_message`),
      life: 4000,
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancelOpenShift() {
    this.initialCashAmount.setValue(0);
    this.showOpenShiftDialog = false;
  }

  cancelCloseShift() {
    this.endCashAmount.setValue(0);
    this.showCloseShiftDialog = false;
  }

  onCloseShift() {
    this.store.dispatch(globalRulesGetCloseShiftOrderSummary());
    this.showCloseShiftDialog = true;
  }

  showDeactivationModal() {
    this.deactivationModal.show();
  }

  confirmOpenShift() {
    this.store.dispatch(
      globalRulesOpenShift({ openAmount: this.initialCashAmount.value! }),
    );
    this.initialCashAmount.setValue(0);
    this.showOpenShiftDialog = false;
  }

  confirmCloseShift() {
    this.store.dispatch(
      globalRulesCloseShift({ closeAmount: this.endCashAmount.value! }),
    );
    this.endCashAmount.setValue(0);
    this.showCloseShiftDialog = false;
  }

  getCashDifference(): number {
    const summary = this.closeShiftOrderSummaryData;
    const expectedCash =
      (summary?.startCash ?? 0) +
      this.getPaymentMethodTotal('CASH') -
      this.getTotalRefund();
    return (this.endCashAmount.value ?? 0) - expectedCash;
  }

  getPaymentMethodTotal(payType: string): number {
    const summary = this.closeShiftOrderSummaryData;
    return (
      summary?.details
        ?.filter(
          (order: any) => order.payType === payType && order.buyerRealPrice > 0,
        )
        .reduce(
          (sum: number, order: any) => sum + (order.buyerRealPrice || 0),
          0,
        ) || 0
    );
  }

  getPaymentMethodLabel(payType: string): string {
    const keyMap: Record<string, string> = {
      CASH: 'payment.cash',
      CARD: 'payment.card',
      E_WALLET: 'payment.e_wallet',
      BANK_TRANSFER: 'payment.bank_transfer',
      QR_CODE: 'payment.qr_code',
    };
    const key = keyMap[payType];
    return key ? this.translateService.instant(key) : payType;
  }

  getPaymentMethodSeverity(
    payType: string,
  ): 'success' | 'info' | 'warning' | 'danger' {
    const severities: {
      [key: string]: 'success' | 'info' | 'warning' | 'danger';
    } = {
      CASH: 'success',
      CARD: 'info',
      E_WALLET: 'warning',
      BANK_TRANSFER: 'info',
      QR_CODE: 'warning',
    };
    return severities[payType] || 'info';
  }

  getTotalRefund(): number {
    const summary = this.closeShiftOrderSummaryData;
    return summary?.totalReturnAmount || 0;
  }

  onLogout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
